import {
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Modal as NModal,
  type ModalProps as Props,
  tv,
} from '@heroui/react';
import type { ReactNode } from 'react';
import { CgDanger } from 'react-icons/cg';
import { FaRegCircleCheck } from 'react-icons/fa6';
import { IoWarningOutline } from 'react-icons/io5';
import { MdOutlineInfo } from 'react-icons/md';
import { VscError } from 'react-icons/vsc';
import { Button } from '../..';

const confirm = tv({
  slots: {
    iconWrapper: 'p-2 bg-danger-50 rounded-full flex justify-center items-center',
    successButton: '',
    titleClass: 'capitalize text-default-800',
    messageClass: 'text-default-500',
    successText: '',
  },
  variants: {
    type: {
      warn: {
        iconWrapper: 'bg-warning-100',
        successButton: 'bg-warning-400',
        successText: 'text-content1',
      },
      success: {
        iconWrapper: 'bg-success-50',
        successButton: 'bg-success-500',
        successText: 'text-content1',
      },
      error: { iconWrapper: 'bg-danger-50', successButton: 'bg-danger-500', successText: 'text-white' },
      info: {
        iconWrapper: 'bg-sky-200 dark:bg-sky-900',
        successButton: 'bg-sky-500 dark:bg-sky-900',
        successText: 'text-content1 dark:text-white',
      },
      danger: {
        iconWrapper: 'bg-danger-50',
        successButton: 'bg-danger-500',
        successText: 'text-white',
      },
      default: {
        iconWrapper: 'bg-default-200',
        successButton: 'bg-default-500',
        successText: 'text-content1',
      },
    },
    size: {
      sm: { titleClass: 'text-sm', messageClass: 'text-xs' },
      md: { titleClass: 'text-base', messageClass: 'text-sm' },
      lg: { titleClass: 'text-lg', messageClass: 'text-lg' },
    },
  },
});

// type ConfirmVariants = VariantProps<typeof confirm>;
type ConfirmVariants = {
  type?: 'warn' | 'success' | 'error' | 'info' | 'danger' | 'default';
  size?: 'sm' | 'md' | 'lg';
};

interface ConfirmProps extends Omit<Props, 'children' | 'size'>, ConfirmVariants {
  title: string;
  message: ReactNode;
  isOpen: boolean;
  onConfirm: () => void | Promise<void> | Promise<unknown>;
  onOpenChange?: (isOpen: boolean) => void;
  children?: Props['children'];
  isLoading?: boolean;
}

const Confirm = ({
  title,
  message,
  isOpen,
  size = 'md',
  type = 'warn',
  onConfirm,
  onOpenChange,
  isLoading,
  ...props
}: ConfirmProps) => {
  const { iconWrapper, successButton, successText, titleClass, messageClass } = confirm();

  const mapping = {
    warn: <IoWarningOutline className='text-warning-700 w-6 h-6' />,
    success: <FaRegCircleCheck className='text-success-700 w-6 h-6' />,
    error: <VscError className='text-danger-700 w-6 h-6' />,
    info: <MdOutlineInfo className='text-sky-700 dark:text-sky-200 w-6 h-6' />,
    danger: <CgDanger className='text-danger-700 w-6 h-6' />,
    default: <MdOutlineInfo className='text-default-700 w-6 h-6' />,
  };

  const handleConfirm = (onClose: () => void) => {
    const result = onConfirm();
    if (result instanceof Promise) {
      result.then(() => {
        onClose();
      });
    } else {
      onClose();
    }
  };

  return (
    <NModal
      // classNames={{ backdrop: 'dark:bg-primary-50/70 dark:opacity-50' }}
      radius='sm'
      {...props}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className='flex flex-row space-x-4 items-center'>
              <span className={iconWrapper({ type })}>{mapping[type]}</span>
              <p className={titleClass({ size })}>{title}</p>
            </ModalHeader>
            <ModalBody>
              <div className={messageClass({ size })}>{message}</div>
            </ModalBody>
            <ModalFooter className=''>
              <Button className='rounded-lg' radius='lg' color='default' variant='light' onPress={onClose}>
                Cancel
              </Button>
              <Button
                radius='sm'
                className={successButton({ type })}
                onPress={() => handleConfirm(onClose)}
                isLoading={isLoading}
              >
                <span className={successText({ type })}>Confirm</span>
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </NModal>
  );
};

export default Confirm;
